<!-- =========================================================================================
  File Name: ECommerceShop.vue
  Description: eCommerce Shop Page
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
    Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div>
    <div id="main-container">
      <div class="algolia-header mb-4">
        <div class="flex md:items-end items-center justify-between flex-wrap">
          <!-- TOGGLE SIDEBAR BUTTON -->
          <feather-icon
            class="inline-flex lg:hidden cursor-pointer mr-4"
            icon="MenuIcon"
            @click.stop="toggleFilterSidebar"/>

          <p class="lg:inline-flex hidden font-semibold algolia-filters-label">Filters</p>

          <div class="flex justify-between items-end flex-grow">
            <!-- Stats -->
            <p class="font-semibold md:block hidden">
              <span v-if="!(!!searchText) && !isSearchStalled">{{ searchFilters.pagination.total }} results found</span>
            </p>

            <div class="flex flex-wrap">

              <!-- ITEMS PER PAGE -->
              <p class="font-semibold md:block hidden mt-3 mr-2">
                <span>Results Per Page:</span>
              </p>
              <vs-select
                v-model="searchFilters.pagination.count"
                @input="fetchProducts"
                class="mr-4 vs-input-shadow-drop vs-select-no-border d-theme-input-dark-bg w-48">
                <vs-select-item
                  v-for="item in resultsPerPageOptions"
                  :key="item.value" :value="item.value" :text="item.label"/>
              </vs-select>
            </div>
          </div>
        </div>
      </div>

      <div id="algolia-content-container" class="relative clearfix">
        <vs-sidebar
          class="items-no-padding vs-sidebar-rounded background-absolute"
          parent="#algolia-content-container"
          :click-not-close="clickNotClose"
          :hidden-background="clickNotClose"
          v-model="isFilterSidebarActive">

          <div class="p-6 filter-container">

            <!-- Add New Product -->
            <vs-button class="ml-auto w-full" color="primary" type="border" icon-after icon="add" @click="showCreateProductDialog = true">Add New Product</vs-button>

            <vs-button id="button-with-loading" ref="loadableButton" class="ml-auto w-full mt-4 vs-con-loading__container" color="primary" type="border" :disabled="isGeneratingCsv" @click="generateCsv">
              Export CSV
            </vs-button>

            <vs-divider/>

            <!-- Status -->
            <h6 class="font-bold mb-4">Status</h6>
            <ul>
              <li v-for="item in activeOptions" :key="item.value"
                  class="mb-2 flex items-center justify-between">
                <vs-checkbox v-model="item.isSelected" class="ml-0" @change="fetchProducts()">
                  {{ item.label }}
                </vs-checkbox>
              </li>
            </ul>
            <vs-divider/>

            <h6 class="font-bold mb-4">Frequency</h6>
            <vs-select v-model="setFrequency" @change="fetchProducts()" class="mr-4 vs-input-shadow-drop vs-select-no-border vx-col">
                <vs-select-item
                  v-for="(freqOption, index) in frequencyOptions"
                  :key="`${index}-freqOption`"
                  :value="freqOption.value"
                  :text="freqOption.label"
                >
                  {{ freqOption.label }}
                </vs-select-item>
            </vs-select>
            <vs-divider/>

            <!-- Types -->
            <h6 class="font-bold mb-4">Product Type</h6>
            <ul>
              <!--              <li v-if="!companies.length">Error fetching companies.</li>-->
              <li v-for="item in productTypes" :key="item.product_type_id"
                  class="mb-2 flex items-center justify-between">
                <vs-checkbox v-model="item.isSelected" class="ml-0" @change="fetchProducts()">
                  {{ item.name }}
                </vs-checkbox>
              </li>
            </ul>
            <vs-divider/>

            <!-- Companies -->
            <h6 class="font-bold mb-4">Company</h6>
            <ul>
              <li v-if="!companies.length">Error fetching companies.</li>
              <li v-for="item in companies" :key="item.company_id"
                  class="mb-2 flex items-center justify-between">
                <vs-checkbox v-model="item.isSelected" class="ml-0" @change="fetchProducts()">
                  {{ item.name }}
                </vs-checkbox>
              </li>
            </ul>
            <vs-divider/>


            <div class="flex justify-center">
              <vs-button class="w-full" @click="clearFilters" :disabled="!hasFilters">
                Clear Filters
              </vs-button>
            </div>
          </div>
        </vs-sidebar>

        <!-- RIGHT COL -->
        <div :class="{'sidebar-spacer-with-margin': clickNotClose}">

          <!-- SEARCH BAR -->
          <div class="relative mb-8">

            <!-- SEARCH INPUT -->
            <vs-input class="w-full vs-input-shadow-drop vs-input-no-border d-theme-input-dark-bg"
                      type="text"
                      placeholder="Search here"
                      size="large"
                      v-model="searchText"
                      v-on:keyup.enter="fetchProducts"
                      v-on:keyup.esc="searchText = ''"
            />

            <!-- SEARCH LOADING -->
            <p :hidden="!isSearchStalled" class="mt-4 text-grey">
              <feather-icon icon="ClockIcon" svgClasses="w-4 h-4" class="mr-2 align-middle"/>
              <span>Loading...</span>
            </p>

            <!-- SEARCH ICON -->
            <div slot="submit-icon" class="absolute top-0 right-0 py-4 px-6"
                 v-show="!(!!searchText)">
              <feather-icon icon="SearchIcon" svgClasses="h-6 w-6"/>
            </div>

            <!-- CLEAR INPUT ICON -->
            <div slot="reset-icon" class="absolute top-0 right-0 py-4 px-6"
                 v-show="!!searchText">
              <feather-icon icon="XIcon" svgClasses="h-6 w-6 cursor-pointer" @click="clearFilters"/>
            </div>
          </div>


          <!-- SEARCH RESULT -->
          <div :hidden="isSearchStalled" class="items-list-view" v-for="item in products"
               :key="item.product_id">

            <ItemListView :product="item" v-on:editProduct="goToProductDetail">

              <!--              &lt;!&ndash; SLOT: ACTION BUTTONS &ndash;&gt;-->
              <!--              <template slot="action-buttons">-->
              <!--                <div-->
              <!--                  class="item-view-primary-action-btn p-3 rounded-lg flex flex-grow items-center justify-center cursor-pointer mb-3"-->
              <!--                  @click="toggleItemInWishList(item)">-->
              <!--                  <feather-icon icon="HeartIcon"-->
              <!--                                :svgClasses="[{'text-danger fill-current' : isInWishList(item.product_id)}, 'h-4 w-4']"/>-->
              <!--                  <span class="text-sm font-semibold ml-2">WISHLIST</span>-->
              <!--                </div>-->
              <!--                <div-->
              <!--                  class="item-view-secondary-action-btn bg-primary p-3 rounded-lg flex flex-grow items-center justify-center text-white cursor-pointer"-->
              <!--                  @click="cartButtonClicked(item)">-->
              <!--                  <feather-icon icon="ShoppingBagIcon" svgClasses="h-4 w-4"/>-->

              <!--                  <span class="text-sm font-semibold ml-2" v-if="isInCart(item.product_id)">VIEW IN CART</span>-->
              <!--                  <span class="text-sm font-semibold ml-2" v-else>ADD TO CART</span>-->
              <!--                </div>-->
              <!--              </template>-->
            </ItemListView>

          </div>

          <!-- PAGINATION -->
          <vs-pagination
            :total="pageCount"
            :max="7"
            :value="searchFilters.pagination.page"
            @input="(val) => { searchFilters.pagination.page = val; fetchProducts(false); }"/>
        </div>
      </div>
    </div>


    <vs-prompt
      buttons-hidden
      title="New Product"
      :active.sync="showCreateProductDialog">
      <div class="modal-lg">
        <ProductGeneralEditor
          :product="new models.Product()"
          :companies="companies"
          :all-product-types="productTypes"
          v-on:productChanged="goToProductDetail($event.product_id)"
        />
      </div>
    </vs-prompt>

  </div>
</template>

<script>
  import api from '@/http/api';
  import models from '@/models';
  import ProductGeneralEditor from './components/ProductGeneralEditorCard';
  import moment from "moment";
  import {ExportToCsv} from "export-to-csv";

  export default {
    components: {
      ProductGeneralEditor,
      ItemListView: () => import("./components/ProductListItem.vue"),
    },
    data() {
      return {
        showCreateProductDialog: false,
        isGeneratingCsv: false,
        models: models,
        isSearchStalled: false,
        currentRefinement: true,
        setFrequency: null,
        companies: [new models.Company()],
        resultsPerPageOptions: [
          {label: '25', value: 25},
          {label: '50', value: 50},
          {label: '100', value: 100}
        ],
        activeOptions: [
          {label: 'Active', value: 1},
          {label: 'Inactive', value: 0},
        ],
        frequencyOptions: [
          {label: 'All', value: null},
          {label: 'Has Frequency', value: true},
          {label: 'No Frequency', value: false}
        ],
        productTypes: [
          new models.ProductType({ product_type_id: 1, name: 'Oils' }),
          new models.ProductType({ product_type_id: 2, name: 'Supplement' }),
          new models.ProductType({ product_type_id: 3, name: 'Blend' }),
          new models.ProductType({ product_type_id: 4, name: 'Kit' }),
        ],
        searchText: '',
        searchFilters: {
          filters: {
            active: null,
            companies: [],
            product_types: [],
            search: '',
          },
          pagination: {
            total: 0,
            count: 25,
            page: 1,
          },
        },
        products: [],
        filtersLoadedFromStore: false,

        // Filter Sidebar
        isFilterSidebarActive: true,
        clickNotClose: true,
        fetchProductCount: 0,
      }
    },
    mounted() {
      this.fetchCompanies().then(() => {
        this.loadFiltersFromStore();
        this.fetchProducts();
      });
    },
    computed: {
      pageCount() {
        let total = this.searchFilters.pagination.total;
        let perPage = this.searchFilters.pagination.count;
        let pages = Math.floor(total / perPage);
        let plus1 = total % perPage !== 0;
        if (plus1) pages++;
        return pages;
      },

      windowWidth() {
        return this.$store.state.windowWidth
      },

      hasFilters() {
        if (this.searchFilters.filters.search) return true;
        if (this.searchFilters.filters.companies.length > 0) return true;
        if (this.searchFilters.filters.active !== null) return true;
        if (this.searchFilters.filters.product_types.length > 0) return true;

        return false;
      },
    },
    watch: {
      windowWidth() {
        this.setSidebarWidth()
      }
    },
    methods: {
      isInCart() {
        return false;
      },
      isInWishList() {
        return false;
      },
      setSidebarWidth() {
        if (this.windowWidth < 992) {
          this.isFilterSidebarActive = this.clickNotClose = false
        } else {
          this.isFilterSidebarActive = this.clickNotClose = true
        }
      },
      clearFilters() {
        this.searchFilters.filters.search = '';
        this.searchFilters.filters.companies = [];
        this.searchFilters.pagination.count = this.resultsPerPageOptions[0].value;
        this.searchFilters.pagination.page = 1;

        this.searchText = '';
        this.companies.forEach((company) => {
          company.isSelected = false;
        });
        this.searchFilters.filters.active = null;
        this.activeOptions.forEach((option) => {
          option.isSelected = false;
        });
        this.searchFilters.filters.product_types = [];
        this.productTypes.forEach((type) => {
          type.isSelected = false;
        });

        this.fetchProducts();
      },
      async goToProductDetail(productId) {
        this.showCreateProductDialog = false;
        // just give the UI enough time to close the dialog
        await new Promise(resolve => setTimeout(resolve, 300));
        this.$router.push(`/content/products/detail?id=${productId}`).catch(()=> {
        });
      },
      async fetchProducts(resetPage = true) {
        if (!this.filtersLoadedFromStore) return;
        if (this.searchText && !isNaN(this.searchText)) {
          return this.goToProductDetail(this.searchText);
        }

        this.isSearchStalled = true;
        const fetchNumber = ++this.fetchProductCount;

        let productsObj = await api.fetchProducts(await this.buildSearchFilters());
        if (fetchNumber === this.fetchProductCount) {
          this.products = productsObj.products;
          if (resetPage) this.searchFilters.pagination.page = 1;
          this.searchFilters.pagination.total = productsObj.pagination.total;
        }

        this.isSearchStalled = false;
      },

      async fetchCompanies() {
        let companiesObj = await api.fetchAllCompanies();

        this.companies = companiesObj.companies;
      },
      loadFiltersFromStore(){
        this.searchFilters = this.$store.state.productListFiltersJson ? JSON.parse(this.$store.state.productListFiltersJson) : this.searchFilters;

        this.companies.forEach((company) => {
          if (this.searchFilters.filters.companies.indexOf(company.company_id) !== -1)
            company.isSelected = true;
        });

        this.productTypes.forEach((type) => {
          if (this.searchFilters.filters.product_types.indexOf(type.product_type_id) !== -1)
            type.isSelected = true;
        });

        this.activeOptions.forEach((activeOption) => {
          if (this.searchFilters.filters.active === null) {
            activeOption.isSelected = false;
          } else if (this.searchFilters.filters.active) {
            if (activeOption.label === 'Active') activeOption.isSelected = true;
          } else {
            if (activeOption.label === 'Inactive') activeOption.isSelected = true;
          }
        });

        this.searchText = this.searchFilters.filters.search;

        this.filtersLoadedFromStore = true;
      },
      async buildSearchFilters() {
        // set companies filter
        this.searchFilters.filters.companies = this.companies.filter((company) => {
          return company.isSelected;
        }).map((company) => {
          return company.company_id;
        });
        // set product_types filter
        this.searchFilters.filters.product_types = this.productTypes.filter((type) => {
          return type.isSelected;
        }).map((type) => {
          return type.product_type_id;
        });
        // set active filter
        let selectedActive = this.activeOptions.filter((option) => {return option.isSelected});
        if (selectedActive.length !== 1) {
          this.searchFilters.filters.active = null;
        } else {
          this.searchFilters.filters.active = selectedActive[0].value;
        }
        this.searchFilters.filters.search = this.searchText;

        if (this.setFrequency != null) {
          this.searchFilters.filters.hasFrequency = this.setFrequency;
        }
        else {
          delete this.searchFilters.filters.hasFrequency;
        }

        await this.$store.dispatch('updateProductListFilters', JSON.stringify(this.searchFilters));

        return this.searchFilters;
      },

      toggleFilterSidebar() {
        if (this.clickNotClose) return;
        this.isFilterSidebarActive = !this.isFilterSidebarActive
      },

      async generateCsv() {
        this.isGeneratingCsv = true;
        this.$vs.loading({
          container: "#button-with-loading",
          scale: 0.45
        });

        let searchFilters = await this.buildSearchFilters();
        searchFilters.pagination.page = 1;
        searchFilters.pagination.count = this.searchFilters.pagination.total;
        const productsObj = await api.fetchProducts(searchFilters);
        const products = productsObj.products;

        const fileName = moment().format("MMMM Do YYYY") + ` Products`;
        const csvHeaders = ['ID', 'Name', 'Name TM', 'Active', 'Type', 'Min Age', 'Max Age', 'Gender', 'Pregnant or Nursing'];

        const csvExporter = new ExportToCsv({
          filename: fileName,
          showLabels: true,
          showTitle: true,
          title: fileName,
          useKeysAsHeaders: false,
          headers: csvHeaders
        });
        const csvData = this.formatForCsv(products)
        csvExporter.generateCsv(csvData);

        this.$vs.loading.close("#button-with-loading > .con-vs-loading");
        this.isGeneratingCsv = false;
      },
      formatForCsv(products) {
        return products.map(product => {
          const localizations = product.localizations.filter(localization => localization.language.language_id === 1);
          const localization = localizations.length ? localizations[0] : null;
          return {
            id: product.product_id,
            name: localization ? localization.product_name : "",
            nameTm: localization ? localization.product_name_tm : "",
            active: product.active ? "Yes" : "No",
            type: product.product_type.name,
            minAge: product.min_age || "",
            maxAge: product.max_age || "",
            gender: product.gender === null ? "" : product.gender === 0 ? "F" : "M",
            preg_or_nurse: product.preg_or_nurse ? "Yes" : "No",
          }
        })
      },
    },
    created() {
      this.setSidebarWidth()
    }
  }

</script>


<style lang="scss">
  #main-container {
    .algolia-header {
      .algolia-filters-label {
        width: calc(260px + 2.4rem);
      }
    }

    #algolia-content-container {

      .vs-sidebar {
        position: relative;
        float: left;
      }
    }

    .algolia-search-input-right-aligned-icon {
      padding: 1rem 1.5rem;
    }

    .algolia-price-slider {
      min-width: unset;
    }

    .item-view-primary-action-btn {
      color: #2c2c2c !important;
      background-color: #f6f6f6;
      min-width: 50%;
    }

    .item-view-secondary-action-btn {
      min-width: 50%;
    }
  }

  .theme-dark {
    #main-container {
      #algolia-content-container {
        .vs-sidebar {
          background-color: #10163a;
        }
      }
    }
  }

  @media (min-width: 992px) {
    .vs-sidebar-rounded {
      .vs-sidebar {
        border-radius: .5rem;
      }

      .vs-sidebar--items {
        border-radius: .5rem;
      }
    }
  }

  @media (max-width: 992px) {
    #algolia-content-container {
      .vs-sidebar {
        position: absolute !important;
        float: none !important;
      }
    }
  }

</style>

